import React, { useEffect, useState } from 'react';
import { UserPlus, Shield, Ban, CheckCircle, Trash2, Edit2, X, Loader2, Mail, MapPin, RefreshCw, Users as UsersIcon, Copy, Key } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi, teamsApi, UserRecord, UserRegionRow } from '../services/creatorApi';
import { generateSalt, hashPassword } from '../services/authService';
import { toast } from 'sonner';
import { CreateUserModal } from '../components/CreateUserModal';
import { ConfirmModal } from '../components/ConfirmModal';
export function Users() {
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const [allTeamUsers, setAllTeamUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Viewer' as UserRecord['Role'],
    regions: [] as string[],
    status: 'Active' as UserRecord['Status']
  });
  const canManageUsers = currentUser.Role === 'Super Admin' || currentUser.Role === 'Team Manager';
  const loadData = async () => {
    setLoading(true);
    try {
      // Always fetch users for the current team
      const fetchedUsers = await usersApi.findByTeam(currentWorkspace.name);
      setAllTeamUsers(fetchedUsers);
      // Fetch Team Regions
      const team = await teamsApi.findByTeamName(currentWorkspace.name);
      if (team?.Team_Regions) {
        setAvailableRegions(team.Team_Regions.map(r => r.Region_Name));
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, [currentWorkspace.name]);
  // Filter by region if a sub-region is selected in the header scope
  const filteredUsers = currentSubRegion ? allTeamUsers.filter(user => {
    // Show users who have no regions (access to all) OR have the selected region
    if (!user.User_Regions || user.User_Regions.length === 0) return true;
    return user.User_Regions.some(r => r.Region_Name === currentSubRegion);
  }) : allTeamUsers;
  const handleOpenModal = (user?: UserRecord) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.User_Name,
        email: user.Email,
        role: user.Role,
        regions: user.User_Regions?.map(r => r.Region_Name) || [],
        status: user.Status
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        role: 'Viewer',
        regions: [],
        status: 'Active'
      });
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Name and Email are required');
      return;
    }
    setIsSaving(true);
    try {
      // Preserve existing subform row IDs for regions that already exist
      const existingRegions = editingUser?.User_Regions || [];
      const regionRows: UserRegionRow[] = formData.regions.map(r => {
        const existingRow = existingRegions.find(er => er.Region_Name === r);
        return {
          ...(existingRow?.ID ? {
            ID: existingRow.ID
          } : {}),
          Region_Name: r
        };
      });
      const payload = {
        User_Name: formData.name,
        Email: formData.email,
        Role: formData.role,
        Team: currentWorkspace.name,
        Status: formData.status,
        User_Regions: regionRows
      };
      if (editingUser) {
        await usersApi.update(editingUser.ID, payload);
        toast.success('User updated successfully');
        handleCloseModal();
        loadData();
      } else {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
        let pwd = '';
        for (let i = 0; i < 8; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
        const salt = generateSalt();
        const hash = await hashPassword(pwd, salt);
        await usersApi.create({
          ...payload,
          Invited_By: currentUser.Email || currentUser.email || '',
          Password_Hash: hash,
          Password_Salt: salt,
          Password_Set: true,
          Reset_Token: pwd,
          Reset_Token_Expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        });
        setNewUserName(formData.name);
        setNewUserEmail(formData.email);
        setGeneratedPassword(pwd);
        setShowPasswordModal(true);
        handleCloseModal();
        loadData();
      }
    } catch (err) {
      console.error('Failed to save user:', err);
      toast.error('Failed to save user');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = (user: UserRecord) => {
    setUserToDelete(user);
  };
  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await usersApi.delete(userToDelete.ID);
      toast.success('User deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete user:', err);
      toast.error('Failed to delete user');
    }
    setUserToDelete(null);
  };
  const handleToggleStatus = async (user: UserRecord) => {
    const newStatus = user.Status === 'Active' ? 'Suspended' : 'Active';
    try {
      await usersApi.update(user.ID, {
        Status: newStatus
      });
      toast.success(`User ${newStatus === 'Active' ? 'activated' : 'suspended'}`);
      loadData();
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update user status');
    }
  };
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Team Manager':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Operator':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-6903" data-mp="3gdaf3njhaqvx">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-6904" data-mp="1vvhnie9paqvt" />
      </div>;
  }
  return <div className="space-y-6" data-id="element-6905" data-mp="20mrd5ev91u7p">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6906" data-mp="20o2e76xb8nv4">
        <div className="min-w-0" data-id="element-6907" data-mp="1lywme40sxzx9">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6908" data-mp="1lvquyi4palyd">Team Users</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6909" data-mp="1qwy2qh6ufl51">
            {currentSubRegion ? `Users with access to ${currentSubRegion} in ${currentWorkspace.name}.` : `Manage users for ${currentWorkspace.name}.`}
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-6910" data-mp="25mcajkurtuib">
          <button onClick={loadData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-6911" data-mp="25nnepls4p49y">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6912" data-mp="u8rz9xlnqysc" />
          </button>
          {canManageUsers && <button onClick={() => handleOpenModal()} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0 shadow-sm transition-colors" data-id="element-6913" data-mp="1pvu62gfc9gvh">
              <UserPlus className="h-4 w-4 flex-shrink-0" data-id="element-6914" data-mp="1bw2ltxy8b491" />
              <span data-id="element-6915" data-mp="1bxnd9ep8y72o">Invite User</span>
            </button>}
        </div>
      </div>

      {/* Summary bar */}
      {currentSubRegion && <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm" data-id="element-6916" data-mp="24jygyy85go3p">
          <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" data-id="element-6917" data-mp="1zsq57vjckqnd" />
          <span className="text-blue-800" data-id="element-6918" data-mp="1uuwzf3lyhynm">
            Showing <strong data-id="element-6919" data-mp="1uu5dylm2phb2">{filteredUsers.length}</strong> user
            {filteredUsers.length !== 1 ? 's' : ''} with access to{' '}
            <strong data-id="element-6920" data-mp="2sbvr8kvyb82p">{currentSubRegion}</strong>
            {allTeamUsers.length !== filteredUsers.length && <span className="text-blue-600 ml-1" data-id="element-6921" data-mp="2uuh0n2y93fle">
                ({allTeamUsers.length} total in team)
              </span>}
          </span>
        </div>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm" data-id="element-6922" data-mp="2zvo810whecrj">
        {filteredUsers.length === 0 ? <div className="p-12 text-center" data-id="element-6923" data-mp="38urpy1jwwm4g">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6924" data-mp="38vtbizyk0r0f" />
            <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-6925" data-mp="3du4ldaoun1in">
              {currentSubRegion ? `No users found for ${currentSubRegion}` : 'No users found'}
            </h3>
            <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-6926" data-mp="3nkh5x2wnyuos">
              {currentSubRegion ? `No team members have access to the ${currentSubRegion} region. Invite a user or update an existing user's region access.` : 'Invite team members to collaborate on this workspace.'}
            </p>
            {canManageUsers && <button onClick={() => handleOpenModal()} className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6927" data-mp="1s70yd5z4230n">
                <UserPlus className="h-4 w-4" data-id="element-6928" data-mp="223x08sui7qw8" />
                Invite User
              </button>}
          </div> : <div className="overflow-x-auto" data-id="element-6929" data-mp="2ax0jjhrtyxys">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6930" data-mp="2avyxki9a0r2a">
              <thead className="bg-gray-50" data-id="element-6931" data-mp="2fvtmwhdel8l8">
                <tr data-id="element-6932" data-mp="2fuihkd4b7ss0">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6933" data-mp="2fy6d1htbep9d">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6934" data-mp="2ptrb54v9j5du">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6935" data-mp="vh9vojgo19jr">
                    Regions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6936" data-mp="33zjg82npf5pb">
                    Status
                  </th>
                  {canManageUsers && <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6937" data-mp="2ux0g3qbaz76r">
                        Reset Code
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6938" data-mp="3nlo6g0awsmr6">
                        Actions
                      </th>
                    </>}
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6939" data-mp="27b3qo6klgwc7">
                {filteredUsers.map(user => <tr key={user.ID} className="hover:bg-gray-50 transition-colors" data-id="element-6940" data-mp="2294oh00lc5gu">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6941" data-mp="28r7qr607pm7v">
                      <div className="flex items-center" data-id="element-6942" data-mp="28obudm40j5lj">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-sm font-medium text-white mr-3 flex-shrink-0 shadow-sm" data-id="element-6943" data-mp="1uq534otkk3uu">
                          {user.User_Name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0" data-id="element-6944" data-mp="1pslnbkh83j0s">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-6945" data-mp="1c0p1tza287dd">
                            {user.User_Name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-[200px] flex items-center gap-1" data-id="element-6946" data-mp="1fzxrary2hj34">
                            <Mail className="h-3 w-3" data-id="element-6947" data-mp="171celdfa0ya9" />
                            {user.Email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6948" data-mp="igphvyl6tdz4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.Role)}`} data-id="element-6949" data-mp="iik5jknufk6w">
                        <Shield className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-6950" data-mp="sgq5vxu04i9n" />
                        {user.Role}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6951" data-mp="11eab1huziypj">
                      <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-6952" data-mp="11dh9cvavuze9">
                        {user.User_Regions && user.User_Regions.length > 0 ? user.User_Regions.map((r, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-6953" data-mp="1p4mof8901nni" data-mpi={i}>
                              {r.Region_Name}
                            </span>) : <span className="text-xs text-gray-400 italic" data-id="element-6954" data-mp="3oc34uj9rutug">
                            All Regions
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6955" data-mp="2vnyyrwnager8">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${user.Status === 'Active' ? 'bg-green-50 text-green-700 border-green-200' : user.Status === 'Suspended' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`} data-id="element-6956" data-mp="30m8uc8cxdhdd">
                        {user.Status === 'Active' && <CheckCircle className="h-3 w-3 mr-1" data-id="element-6957" data-mp="ow6fccpaytbm" />}
                        {user.Status === 'Suspended' && <Ban className="h-3 w-3 mr-1" data-id="element-6958" data-mp="29gutyf9e3xle" />}
                        {user.Status === 'Invited' && <Mail className="h-3 w-3 mr-1" data-id="element-6959" data-mp="20lwx4x34g0os" />}
                        {user.Status}
                      </span>
                    </td>

                    {canManageUsers && <>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-6960" data-mp="1qptu7p0g3y0s">
                          {user.Reset_Token && user.Reset_Token_Expires && new Date(user.Reset_Token_Expires) > new Date() ? <div className="flex items-center gap-2" data-id="element-6961" data-mp="1cp10v7lvr8z9">
                              <span className="font-mono text-sm font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200" data-id="element-6962" data-mp="1cqu7wo0env7s">
                                {user.Reset_Token}
                              </span>
                              <span className="text-xs text-gray-400" data-id="element-6963" data-mp="3q6vo9cb7lxmt">
                                Expires{' '}
                                {new Date(user.Reset_Token_Expires).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                              </span>
                            </div> : <span className="text-xs text-gray-400 italic" data-id="element-6964" data-mp="hq0vwqs1fvmz">
                              None
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6965" data-mp="8pcgbrmzxn7z">
                          <div className="flex items-center justify-end gap-2" data-id="element-6966" data-mp="2oh0d9n20ffqs">
                            <button onClick={() => handleOpenModal(user)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Edit User" data-id="element-6967" data-mp="2th4sl9r6m4ff">
                              <Edit2 className="h-4 w-4" data-id="element-6968" data-mp="2uwz5etd53193" />
                            </button>
                            {user.Status === 'Active' ? <button onClick={() => handleToggleStatus(user)} className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors" title="Suspend User" data-id="element-6969" data-mp="229cqioygoa6x">
                                <Ban className="h-4 w-4" data-id="element-6970" data-mp="2787iageep936" />
                              </button> : <button onClick={() => handleToggleStatus(user)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="Activate User" data-id="element-6971" data-mp="1jhag3vcexq54">
                                <CheckCircle className="h-4 w-4" data-id="element-6972" data-mp="y1dc1yk7yvzx" />
                              </button>}
                            <button onClick={() => handleDelete(user)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete User" data-id="element-6973" data-mp="17y9cri3vmdtx">
                              <Trash2 className="h-4 w-4" data-id="element-6974" data-mp="1louuv4liajqr" />
                            </button>
                          </div>
                        </td>
                      </>}
                  </tr>)}
              </tbody>
            </table>{' '}
          </div>}
      </div>

      {/* Create/Edit User Modal */}
      {isModalOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6975" data-mp="3l46afe050sx9">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6976" data-mp="3l2cz3lekxkix">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal} data-id="element-6977" data-mp="3g3rxpinlcusw" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden transform transition-all" data-id="element-6978" data-mp="367f8oy2zr54h">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-6979" data-mp="7oi0ooyk6frz">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-6980" data-mp="2qymtq5rw50k">
                  {editingUser ? 'Edit User' : 'Invite New User'}
                </h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-6981" data-mp="3p6ax1ss64pae">
                  <X className="h-5 w-5" data-id="element-6982" data-mp="qi42o5bpqxav" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4" data-id="element-6983" data-mp="lnzvbkyjcn99">
                <div data-id="element-6984" data-mp="2cb7rshpxypo6">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6985" data-mp="2cbpvfzexo664">
                    Full Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="e.g. John Doe" data-id="element-6986" data-mp="239glpbrhogts" />
                </div>

                <div data-id="element-6987" data-mp="2h8q32erflgzc">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6988" data-mp="2h7f20mpdenbx">
                    Email Address
                  </label>
                  <input type="email" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="john@example.com" data-id="element-6989" data-mp="39xn9nkma5pc9" />
                </div>

                <div data-id="element-6990" data-mp="40p3u7kqueyw">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6991" data-mp="9133p2c6yilu">
                    Role
                  </label>
                  <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value as any
              })} data-id="element-6992" data-mp="dtnuduzswfl5">
                    <option value="Viewer" data-id="element-6993" data-mp="2nswc4sunyk2d">Viewer</option>
                    <option value="Operator" data-id="element-6994" data-mp="2juym5nt2jtxx">Operator</option>
                    <option value="Team Manager" data-id="element-6995" data-mp="2xksbs8ceei8p">Team Manager</option>
                    {currentUser.Role === 'Super Admin' && <option value="Super Admin" data-id="element-6996" data-mp="2stajs9scrrzv">Super Admin</option>}
                  </select>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-6997" data-mp="37hon0ltoligy">
                    {formData.role === 'Viewer' && 'Read-only access to all data.'}
                    {formData.role === 'Operator' && 'Can manage content but not settings.'}
                    {formData.role === 'Team Manager' && 'Full access within assigned team.'}
                    {formData.role === 'Super Admin' && 'Full access across all teams.'}
                  </p>
                </div>

                <div data-id="element-6998" data-mp="3sufbnnnuc7q8">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6999" data-mp="3sv6z2b7aaz5f">
                    Team
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={currentWorkspace.name} disabled data-id="element-7000" data-mp="1n2zjn1tekh6" />
                </div>

                {availableRegions.length > 0 && <div data-id="element-7001" data-mp="3f3sdpxpwmlv7">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7002" data-mp="3f426tu68x954">
                      Region Access
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50" data-id="element-7003" data-mp="3541jlx59a8vn">
                      {availableRegions.map(region => <label key={region} className="flex items-center gap-2 cursor-pointer" data-id="element-7004" data-mp="13d7xss4ocvf7">
                          <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={formData.regions.includes(region)} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        regions: [...formData.regions, region]
                      });
                    } else {
                      setFormData({
                        ...formData,
                        regions: formData.regions.filter(r => r !== region)
                      });
                    }
                  }} data-id="element-7005" data-mp="ekhih459r3ru" />
                          <span className="text-sm text-gray-700" data-id="element-7006" data-mp="1jkxk372phoqa">
                            {region}
                          </span>
                        </label>)}
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-7007" data-mp="1smyja9hi2kul">
                      Leave empty to grant access to all regions in the team.
                    </p>
                  </div>}

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100 mt-6" data-id="element-7008" data-mp="zvet25mhk6j3">
                  <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-7009" data-mp="14wugg4c4yp4n">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7010" data-mp="h55oip3882o8">
                    {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-7011" data-mp="nvq54es69nxc" />}
                    {editingUser ? 'Save Changes' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      <ConfirmModal isOpen={!!userToDelete} title="Delete User" message={`Are you sure you want to delete ${userToDelete?.User_Name}?`} confirmLabel="Delete" variant="danger" onCancel={() => setUserToDelete(null)} onConfirm={confirmDelete} data-id="element-7012" data-mp="3rc6z69zjnucc" />

      {/* Password Success Modal */}
      {showPasswordModal && generatedPassword && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-7013" data-mp="1gi17nuzc9yws">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-7014" data-mp="1blkhkv8kxah2">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPasswordModal(false)} data-id="element-7015" data-mp="2tinv1pwxdm52" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden transform transition-all p-6 text-center" data-id="element-7016" data-mp="2yf4kcnfv26jq">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4" data-id="element-7017" data-mp="2pg121loipv6a">
                <Key className="h-6 w-6 text-green-600" data-id="element-7018" data-mp="3d7okrgbmlzxh" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-7019" data-mp="3h88gcfgjwvhi">
                User Created Successfully
              </h3>
              <p className="text-sm text-gray-500 mb-6" data-id="element-7020" data-mp="33g2bvqmrgcxw">
                A temporary password has been generated for{' '}
                <strong data-id="element-7021" data-mp="38dkb2paz7dsp">{newUserName}</strong> ({newUserEmail}).
              </p>

              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-6" data-id="element-7022" data-mp="3w3occq0oz1gj">
                <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2" data-id="element-7023" data-mp="4rd9yae70kb5">
                  Generated Password
                </div>
                <div className="flex items-center justify-center gap-3" data-id="element-7024" data-mp="1w39oul4oez3s">
                  <code className="text-2xl font-mono font-bold text-gray-900 bg-white px-4 py-2 rounded border border-gray-200 shadow-sm" data-id="element-7025" data-mp="1w16qndtddznz">
                    {generatedPassword}
                  </code>
                  <button onClick={() => {
                navigator.clipboard.writeText(generatedPassword);
                toast.success('Password copied to clipboard');
              }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Copy password" data-id="element-7026" data-mp="1m2zct3udnxq7">
                    <Copy className="h-5 w-5" data-id="element-7027" data-mp="yf9dzx1o8ojy" />
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-6 text-left flex gap-3" data-id="element-7028" data-mp="udw67hf0qbae">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" data-id="element-7029" data-mp="361j607pb3qf6" />
                <p className="text-sm text-amber-800" data-id="element-7030" data-mp="3a2bjjd57ohh9">
                  <strong data-id="element-7031" data-mp="3a34kfxhhocrh">
                    Save this password — it cannot be retrieved later.
                  </strong>{' '}
                  The user can change it after logging in.
                </p>
              </div>

              <button onClick={() => setShowPasswordModal(false)} className="w-full inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-7032" data-mp="3ju8ymuzcbtl9">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
}